/**
 * AdminVeterinairesContent - Admin component for managing /veterinaires page content
 */

import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  FileText,
  Save,
  RefreshCw,
  Plus,
  Edit,
  Trash2,
  MoreVertical,
  HelpCircle,
  Zap,
  ChevronDown,
  ChevronUp,
  Eye,
  Calendar,
  Video,
  Shield,
  PhoneOff,
  TrendingUp,
  Star,
  Heart,
  Globe,
  Award,
  Users,
  Clock,
  Stethoscope,
  X,
  Rocket,
  MapPin,
  CheckCircle2,
  ArrowRight,
  Monitor,
  Smartphone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_OPTIONS = [
  { value: 'Calendar', label: 'Calendrier', icon: Calendar },
  { value: 'Video', label: 'Vidéo', icon: Video },
  { value: 'FileText', label: 'Document', icon: FileText },
  { value: 'PhoneOff', label: 'Téléphone', icon: PhoneOff },
  { value: 'TrendingUp', label: 'Croissance', icon: TrendingUp },
  { value: 'Shield', label: 'Sécurité', icon: Shield },
  { value: 'Star', label: 'Étoile', icon: Star },
  { value: 'Heart', label: 'Cœur', icon: Heart },
  { value: 'Globe', label: 'Globe', icon: Globe },
  { value: 'Award', label: 'Récompense', icon: Award },
  { value: 'Users', label: 'Utilisateurs', icon: Users },
  { value: 'Clock', label: 'Horloge', icon: Clock },
  { value: 'Stethoscope', label: 'Stéthoscope', icon: Stethoscope },
  { value: 'Zap', label: 'Éclair', icon: Zap },
];

// Icon mapping for preview
const iconMap = {
  Calendar, Video, FileText, PhoneOff, TrendingUp, Shield,
  Star, Heart, Globe, Award, Users, Clock, Stethoscope, Zap
};

// Live Preview Component
const VeterinairesPreview = ({ content }) => {
  if (!content) return null;

  const hero = content.hero || {};
  const features = (content.features || []).filter(f => f.active !== false);
  const founderProgram = content.founder_program || {};
  const faq = content.faq || {};
  const faqItems = (faq.items || []).filter(f => f.active !== false);
  const demoForm = content.demo_form || {};

  const getIcon = (iconName) => iconMap[iconName] || Star;

  return (
    <div className="min-h-full bg-white">
      {/* Hero Section Preview */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,white_1px,transparent_1px)] bg-[length:30px_30px]"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 py-16 relative">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <Stethoscope className="w-4 h-4 text-blue-400" />
                </div>
                <span className="text-blue-400 font-semibold text-sm uppercase tracking-wide">
                  {hero.badge || 'Badge'}
                </span>
              </div>
              
              <h1 className="text-3xl lg:text-4xl font-bold leading-tight mb-4">
                {hero.title || 'Titre'}
                <span className="text-blue-400"> {hero.title_highlight || 'Titre coloré'}</span>
              </h1>
              
              <p className="text-base text-slate-300 mb-6 leading-relaxed">
                {hero.subtitle || 'Sous-titre de la page...'}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3">
                <button className="inline-flex items-center justify-center gap-2 bg-blue-500 text-white px-6 py-3 rounded-xl font-semibold text-sm">
                  {hero.cta_primary_text || 'Bouton principal'}
                  <ArrowRight className="w-4 h-4" />
                </button>
                <button className="inline-flex items-center justify-center gap-2 bg-white/10 text-white px-6 py-3 rounded-xl font-semibold text-sm border border-white/20">
                  {hero.cta_secondary_text || 'Bouton secondaire'}
                </button>
              </div>
            </div>
            
            {/* Founder Card Preview */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20">
              <div className="flex items-center gap-2 mb-3">
                <Rocket className="w-4 h-4 text-yellow-400" />
                <span className="text-yellow-400 font-semibold text-sm">Programme Fondateur</span>
              </div>
              
              <h3 className="text-xl font-bold mb-4">Nîmes</h3>
              
              <div className="bg-white/10 rounded-xl p-3 mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-300 text-sm">Places restantes</span>
                  <span className="text-xl font-bold text-yellow-400">7/10</span>
                </div>
                <div className="w-full bg-white/20 rounded-full h-2">
                  <div className="bg-yellow-400 h-2 rounded-full" style={{ width: '70%' }}></div>
                </div>
              </div>
              
              <ul className="space-y-2 mb-4">
                {(founderProgram.benefits || []).slice(0, 3).map((benefit, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                    <span className="text-slate-200">{benefit}</span>
                  </li>
                ))}
              </ul>
              
              <button className="w-full bg-yellow-500 text-yellow-900 px-4 py-2 rounded-xl font-bold text-sm">
                {founderProgram.cta_text || 'Rejoindre'} – Nîmes
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section Preview */}
      {features.length > 0 && (
        <section className="py-12 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                Tout ce dont votre clinique a besoin
              </h2>
              <p className="text-slate-600">Une solution complète pensée pour les vétérinaires</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {features.map((feature, index) => {
                const IconComponent = getIcon(feature.icon);
                return (
                  <div key={index} className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
                      <IconComponent className="w-5 h-5 text-blue-600" />
                    </div>
                    <h3 className="text-base font-bold text-slate-900 mb-1">{feature.title}</h3>
                    <p className="text-sm text-slate-600">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Founder Program Section Preview */}
      <section className="py-12 bg-gradient-to-b from-blue-900 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Rocket className="w-4 h-4 text-yellow-400" />
                <span className="text-yellow-400 font-semibold text-sm">{founderProgram.badge || 'Badge'}</span>
              </div>
              
              <h2 className="text-2xl font-bold mb-4">{founderProgram.title || 'Titre'}</h2>
              <p className="text-slate-300 mb-6 text-sm">{founderProgram.description || 'Description...'}</p>
              
              <ul className="space-y-3">
                {(founderProgram.benefits || []).map((benefit, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                    </div>
                    <span className="text-slate-200 text-sm">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="bg-white/10 backdrop-blur rounded-xl p-6 border border-white/20">
              <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-400" />
                Villes du programme
              </h3>
              <div className="bg-white/10 rounded-lg p-3">
                <p className="font-semibold">Nîmes</p>
                <p className="text-sm text-slate-300">7 places sur 10</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section Preview */}
      {faqItems.length > 0 && (
        <section className="py-12 bg-white">
          <div className="max-w-3xl mx-auto px-4">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-2">{faq.title || 'FAQ'}</h2>
              <p className="text-slate-600">{faq.subtitle || 'Sous-titre'}</p>
            </div>
            
            <div className="space-y-3">
              {faqItems.map((item, index) => (
                <div key={index} className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-slate-50 font-semibold text-slate-900 flex items-center justify-between">
                    {item.question}
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  </div>
                  <div className="px-4 py-3 text-sm text-slate-600 bg-white">
                    {item.answer}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Demo Form Section Preview */}
      <section className="py-12 bg-slate-900 text-white">
        <div className="max-w-2xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold mb-2">{demoForm.title || 'Formulaire'}</h2>
            <p className="text-slate-300">{demoForm.subtitle || 'Sous-titre'}</p>
          </div>
          
          <div className="bg-white/10 backdrop-blur rounded-xl p-6 border border-white/20">
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-sm text-slate-200 block mb-1">
                  {demoForm.fields?.clinic_name?.label || 'Nom de la clinique'}
                </label>
                <div className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-slate-400 text-sm">
                  {demoForm.fields?.clinic_name?.placeholder || 'Placeholder...'}
                </div>
              </div>
              <div>
                <label className="text-sm text-slate-200 block mb-1">
                  {demoForm.fields?.contact_name?.label || 'Nom du contact'}
                </label>
                <div className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-slate-400 text-sm">
                  {demoForm.fields?.contact_name?.placeholder || 'Placeholder...'}
                </div>
              </div>
            </div>
            
            <button className="w-full bg-blue-500 text-white py-3 rounded-xl font-semibold">
              {demoForm.submit_button_text || 'Envoyer'}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

const AdminVeterinairesContent = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [showPreview, setShowPreview] = useState(false);
  
  // Modal states
  const [faqModalOpen, setFaqModalOpen] = useState(false);
  const [featureModalOpen, setFeatureModalOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [editingFeature, setEditingFeature] = useState(null);
  
  // Form states
  const [faqForm, setFaqForm] = useState({ question: '', answer: '', active: true });
  const [featureForm, setFeatureForm] = useState({ icon: 'Star', title: '', description: '', active: true });

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/content/veterinaires/admin`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setContent(data.content || data);
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      toast.error('Erreur lors du chargement du contenu');
    }
    setLoading(false);
  };

  const saveContent = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/content/veterinaires`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(content)
      });
      
      if (response.ok) {
        toast.success('Contenu sauvegardé');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
    setSaving(false);
  };

  const resetContent = async () => {
    if (!window.confirm('Réinitialiser tout le contenu aux valeurs par défaut ?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/content/veterinaires/reset`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setContent(data.content);
        toast.success('Contenu réinitialisé');
      }
    } catch (error) {
      toast.error('Erreur lors de la réinitialisation');
    }
  };

  const updateHero = (field, value) => {
    setContent(prev => ({
      ...prev,
      hero: { ...prev.hero, [field]: value }
    }));
  };

  const updateFounderProgram = (field, value) => {
    setContent(prev => ({
      ...prev,
      founder_program: { ...prev.founder_program, [field]: value }
    }));
  };

  const updateBenefit = (index, value) => {
    setContent(prev => {
      const benefits = [...(prev.founder_program?.benefits || [])];
      benefits[index] = value;
      return {
        ...prev,
        founder_program: { ...prev.founder_program, benefits }
      };
    });
  };

  const addBenefit = () => {
    setContent(prev => ({
      ...prev,
      founder_program: {
        ...prev.founder_program,
        benefits: [...(prev.founder_program?.benefits || []), 'Nouvel avantage']
      }
    }));
  };

  const removeBenefit = (index) => {
    setContent(prev => ({
      ...prev,
      founder_program: {
        ...prev.founder_program,
        benefits: prev.founder_program.benefits.filter((_, i) => i !== index)
      }
    }));
  };

  const updateFaq = (field, value) => {
    setContent(prev => ({
      ...prev,
      faq: { ...prev.faq, [field]: value }
    }));
  };

  const updateDemoForm = (field, value) => {
    setContent(prev => ({
      ...prev,
      demo_form: { ...prev.demo_form, [field]: value }
    }));
  };

  const updateDemoFormField = (fieldName, prop, value) => {
    setContent(prev => ({
      ...prev,
      demo_form: {
        ...prev.demo_form,
        fields: {
          ...prev.demo_form?.fields,
          [fieldName]: {
            ...prev.demo_form?.fields?.[fieldName],
            [prop]: value
          }
        }
      }
    }));
  };

  // FAQ CRUD
  const handleSaveFaq = () => {
    if (!faqForm.question || !faqForm.answer) {
      toast.error('Question et réponse requises');
      return;
    }
    
    setContent(prev => {
      const items = [...(prev.faq?.items || [])];
      if (editingFaq !== null) {
        items[editingFaq] = { ...items[editingFaq], ...faqForm };
      } else {
        items.push({ id: String(items.length + 1), ...faqForm });
      }
      return {
        ...prev,
        faq: { ...prev.faq, items }
      };
    });
    
    setFaqModalOpen(false);
    setEditingFaq(null);
    setFaqForm({ question: '', answer: '', active: true });
    toast.success(editingFaq !== null ? 'FAQ mise à jour' : 'FAQ ajoutée');
  };

  const handleEditFaq = (index) => {
    const item = content.faq.items[index];
    setFaqForm({ question: item.question, answer: item.answer, active: item.active !== false });
    setEditingFaq(index);
    setFaqModalOpen(true);
  };

  const handleDeleteFaq = (index) => {
    if (!window.confirm('Supprimer cette FAQ ?')) return;
    setContent(prev => ({
      ...prev,
      faq: {
        ...prev.faq,
        items: prev.faq.items.filter((_, i) => i !== index)
      }
    }));
    toast.success('FAQ supprimée');
  };

  // Feature CRUD
  const handleSaveFeature = () => {
    if (!featureForm.title || !featureForm.description) {
      toast.error('Titre et description requis');
      return;
    }
    
    setContent(prev => {
      const features = [...(prev.features || [])];
      if (editingFeature !== null) {
        features[editingFeature] = { ...features[editingFeature], ...featureForm };
      } else {
        features.push({ id: String(features.length + 1), ...featureForm });
      }
      return { ...prev, features };
    });
    
    setFeatureModalOpen(false);
    setEditingFeature(null);
    setFeatureForm({ icon: 'Star', title: '', description: '', active: true });
    toast.success(editingFeature !== null ? 'Fonctionnalité mise à jour' : 'Fonctionnalité ajoutée');
  };

  const handleEditFeature = (index) => {
    const feature = content.features[index];
    setFeatureForm({
      icon: feature.icon || 'Star',
      title: feature.title,
      description: feature.description,
      active: feature.active !== false
    });
    setEditingFeature(index);
    setFeatureModalOpen(true);
  };

  const handleDeleteFeature = (index) => {
    if (!window.confirm('Supprimer cette fonctionnalité ?')) return;
    setContent(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
    toast.success('Fonctionnalité supprimée');
  };

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  if (!content) {
    return <div className="text-center py-8">Erreur de chargement</div>;
  }

  const sections = [
    { id: 'hero', label: 'Section Hero', icon: Eye },
    { id: 'features', label: 'Fonctionnalités', icon: Zap },
    { id: 'founder', label: 'Programme Fondateur', icon: Star },
    { id: 'faq', label: 'FAQ', icon: HelpCircle },
    { id: 'demo', label: 'Formulaire Démo', icon: FileText },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-blue-500" />
            <h2 className="text-xl font-semibold">Contenu Page Vétérinaires</h2>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowPreview(true)} className="border-purple-300 text-purple-600 hover:bg-purple-50">
              <Eye className="w-4 h-4 mr-2" /> Prévisualiser
            </Button>
            <Button variant="outline" onClick={resetContent}>
              <RefreshCw className="w-4 h-4 mr-2" /> Réinitialiser
            </Button>
            <Button onClick={saveContent} disabled={saving} className="bg-blue-500 hover:bg-blue-600 text-white">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Sauvegarde...' : 'Sauvegarder'}
            </Button>
          </div>
        </div>
        
        {/* Section tabs */}
        <div className="flex gap-2 flex-wrap">
          {sections.map((section) => (
            <Button
              key={section.id}
              variant={activeSection === section.id ? 'default' : 'outline'}
              onClick={() => setActiveSection(section.id)}
              className={activeSection === section.id ? 'bg-blue-500 text-white' : ''}
            >
              <section.icon className="w-4 h-4 mr-2" />
              {section.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Live Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-6xl h-[90vh] p-0 overflow-hidden">
          <div className="flex flex-col h-full">
            {/* Preview Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
              <div className="flex items-center gap-3">
                <Eye className="w-5 h-5 text-purple-500" />
                <span className="font-semibold">Prévisualisation en temps réel</span>
                <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">Non sauvegardé</span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setShowPreview(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Preview Content */}
            <div className="flex-1 overflow-auto bg-gray-100">
              <VeterinairesPreview content={content} />
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Hero Section */}
      {activeSection === 'hero' && (
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h3 className="font-semibold text-lg mb-4">Section Hero</h3>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Badge</Label>
              <Input
                value={content.hero?.badge || ''}
                onChange={(e) => updateHero('badge', e.target.value)}
                placeholder="Ex: Espace Professionnel"
              />
            </div>
            <div>
              <Label>Titre (partie normale)</Label>
              <Input
                value={content.hero?.title || ''}
                onChange={(e) => updateHero('title', e.target.value)}
                placeholder="Ex: Modernisez votre"
              />
            </div>
            <div>
              <Label>Titre (partie colorée)</Label>
              <Input
                value={content.hero?.title_highlight || ''}
                onChange={(e) => updateHero('title_highlight', e.target.value)}
                placeholder="Ex: clinique vétérinaire"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Sous-titre</Label>
              <Textarea
                value={content.hero?.subtitle || ''}
                onChange={(e) => updateHero('subtitle', e.target.value)}
                placeholder="Description de la page..."
                rows={3}
              />
            </div>
          </div>
          
          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium mb-3">Boutons d'action</h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Texte bouton principal</Label>
                <Input
                  value={content.hero?.cta_primary_text || ''}
                  onChange={(e) => updateHero('cta_primary_text', e.target.value)}
                  placeholder="Ex: Demander une démo"
                />
              </div>
              <div>
                <Label>Lien bouton principal</Label>
                <Input
                  value={content.hero?.cta_primary_link || ''}
                  onChange={(e) => updateHero('cta_primary_link', e.target.value)}
                  placeholder="Ex: #demo"
                />
              </div>
              <div>
                <Label>Texte bouton secondaire</Label>
                <Input
                  value={content.hero?.cta_secondary_text || ''}
                  onChange={(e) => updateHero('cta_secondary_text', e.target.value)}
                  placeholder="Ex: Se connecter"
                />
              </div>
              <div>
                <Label>Lien bouton secondaire</Label>
                <Input
                  value={content.hero?.cta_secondary_link || ''}
                  onChange={(e) => updateHero('cta_secondary_link', e.target.value)}
                  placeholder="Ex: /espace-veterinaire"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Features Section */}
      {activeSection === 'features' && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg">Fonctionnalités</h3>
            <Button onClick={() => {
              setFeatureForm({ icon: 'Star', title: '', description: '', active: true });
              setEditingFeature(null);
              setFeatureModalOpen(true);
            }}>
              <Plus className="w-4 h-4 mr-2" /> Ajouter
            </Button>
          </div>
          
          <div className="space-y-3">
            {(content.features || []).map((feature, index) => {
              const IconComp = ICON_OPTIONS.find(i => i.value === feature.icon)?.icon || Star;
              return (
                <div key={index} className={`border rounded-lg p-4 ${feature.active === false ? 'opacity-50 bg-gray-50' : ''}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <IconComp className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-medium">{feature.title}</h4>
                        <p className="text-sm text-gray-500">{feature.description}</p>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEditFeature(index)}>
                          <Edit className="w-4 h-4 mr-2" /> Modifier
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDeleteFeature(index)} className="text-red-600">
                          <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Founder Program Section */}
      {activeSection === 'founder' && (
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h3 className="font-semibold text-lg mb-4">Programme Fondateur</h3>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Badge</Label>
              <Input
                value={content.founder_program?.badge || ''}
                onChange={(e) => updateFounderProgram('badge', e.target.value)}
                placeholder="Ex: Programme exclusif"
              />
            </div>
            <div>
              <Label>Titre</Label>
              <Input
                value={content.founder_program?.title || ''}
                onChange={(e) => updateFounderProgram('title', e.target.value)}
                placeholder="Ex: Devenez une Clinique Fondatrice"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea
                value={content.founder_program?.description || ''}
                onChange={(e) => updateFounderProgram('description', e.target.value)}
                rows={3}
              />
            </div>
            <div>
              <Label>Texte du bouton CTA</Label>
              <Input
                value={content.founder_program?.cta_text || ''}
                onChange={(e) => updateFounderProgram('cta_text', e.target.value)}
                placeholder="Ex: Rejoindre le programme"
              />
            </div>
          </div>
          
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium">Avantages</h4>
              <Button variant="outline" size="sm" onClick={addBenefit}>
                <Plus className="w-4 h-4 mr-1" /> Ajouter
              </Button>
            </div>
            <div className="space-y-2">
              {(content.founder_program?.benefits || []).map((benefit, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    value={benefit}
                    onChange={(e) => updateBenefit(index, e.target.value)}
                    className="flex-1"
                  />
                  <Button variant="ghost" size="sm" onClick={() => removeBenefit(index)}>
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* FAQ Section */}
      {activeSection === 'faq' && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-lg">FAQ</h3>
            <Button onClick={() => {
              setFaqForm({ question: '', answer: '', active: true });
              setEditingFaq(null);
              setFaqModalOpen(true);
            }}>
              <Plus className="w-4 h-4 mr-2" /> Ajouter une FAQ
            </Button>
          </div>
          
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label>Titre de la section</Label>
              <Input
                value={content.faq?.title || ''}
                onChange={(e) => updateFaq('title', e.target.value)}
                placeholder="Ex: Questions fréquentes"
              />
            </div>
            <div>
              <Label>Sous-titre</Label>
              <Input
                value={content.faq?.subtitle || ''}
                onChange={(e) => updateFaq('subtitle', e.target.value)}
                placeholder="Ex: Tout ce que vous devez savoir..."
              />
            </div>
          </div>
          
          <div className="space-y-3">
            {(content.faq?.items || []).map((item, index) => (
              <div key={index} className={`border rounded-lg p-4 ${item.active === false ? 'opacity-50 bg-gray-50' : ''}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium">{item.question}</h4>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{item.answer}</p>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEditFaq(index)}>
                        <Edit className="w-4 h-4 mr-2" /> Modifier
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDeleteFaq(index)} className="text-red-600">
                        <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Demo Form Section */}
      {activeSection === 'demo' && (
        <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
          <h3 className="font-semibold text-lg mb-4">Formulaire de Démo</h3>
          
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Titre</Label>
              <Input
                value={content.demo_form?.title || ''}
                onChange={(e) => updateDemoForm('title', e.target.value)}
                placeholder="Ex: Planifier une démo"
              />
            </div>
            <div>
              <Label>Sous-titre</Label>
              <Input
                value={content.demo_form?.subtitle || ''}
                onChange={(e) => updateDemoForm('subtitle', e.target.value)}
                placeholder="Ex: Découvrez comment..."
              />
            </div>
            <div>
              <Label>Texte du bouton</Label>
              <Input
                value={content.demo_form?.submit_button_text || ''}
                onChange={(e) => updateDemoForm('submit_button_text', e.target.value)}
                placeholder="Ex: Demander une démo gratuite"
              />
            </div>
            <div>
              <Label>Message de succès</Label>
              <Input
                value={content.demo_form?.success_message || ''}
                onChange={(e) => updateDemoForm('success_message', e.target.value)}
                placeholder="Ex: Votre demande a été envoyée..."
              />
            </div>
          </div>
          
          <div className="border-t pt-4 mt-4">
            <h4 className="font-medium mb-3">Labels et placeholders des champs</h4>
            <div className="grid md:grid-cols-2 gap-4">
              {['clinic_name', 'contact_name', 'email', 'phone', 'city', 'message'].map((fieldName) => (
                <div key={fieldName} className="border rounded-lg p-3">
                  <p className="text-sm font-medium text-gray-700 mb-2 capitalize">{fieldName.replace('_', ' ')}</p>
                  <div className="space-y-2">
                    <Input
                      value={content.demo_form?.fields?.[fieldName]?.label || ''}
                      onChange={(e) => updateDemoFormField(fieldName, 'label', e.target.value)}
                      placeholder="Label du champ"
                      className="text-sm"
                    />
                    <Input
                      value={content.demo_form?.fields?.[fieldName]?.placeholder || ''}
                      onChange={(e) => updateDemoFormField(fieldName, 'placeholder', e.target.value)}
                      placeholder="Placeholder"
                      className="text-sm"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* FAQ Modal */}
      <Dialog open={faqModalOpen} onOpenChange={setFaqModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingFaq !== null ? 'Modifier la FAQ' : 'Ajouter une FAQ'}</DialogTitle>
            <DialogDescription>Ajoutez une question fréquemment posée</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Question *</Label>
              <Input
                value={faqForm.question}
                onChange={(e) => setFaqForm({...faqForm, question: e.target.value})}
                placeholder="Ex: Combien coûte vetoRDV ?"
              />
            </div>
            <div>
              <Label>Réponse *</Label>
              <Textarea
                value={faqForm.answer}
                onChange={(e) => setFaqForm({...faqForm, answer: e.target.value})}
                placeholder="La réponse détaillée..."
                rows={4}
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={faqForm.active}
                onCheckedChange={(checked) => setFaqForm({...faqForm, active: checked})}
              />
              <Label>Active (visible sur le site)</Label>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setFaqModalOpen(false)}>Annuler</Button>
            <Button onClick={handleSaveFaq} className="bg-blue-500 hover:bg-blue-600 text-white">
              <Save className="w-4 h-4 mr-2" /> Enregistrer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Feature Modal */}
      <Dialog open={featureModalOpen} onOpenChange={setFeatureModalOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingFeature !== null ? 'Modifier la fonctionnalité' : 'Ajouter une fonctionnalité'}</DialogTitle>
            <DialogDescription>Ajoutez une fonctionnalité à mettre en avant</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Icône</Label>
              <div className="grid grid-cols-7 gap-2 mt-2">
                {ICON_OPTIONS.map((option) => {
                  const IconComp = option.icon;
                  return (
                    <button
                      key={option.value}
                      onClick={() => setFeatureForm({...featureForm, icon: option.value})}
                      className={`p-2 rounded-lg border ${featureForm.icon === option.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'}`}
                      title={option.label}
                    >
                      <IconComp className="w-5 h-5 mx-auto" />
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <Label>Titre *</Label>
              <Input
                value={featureForm.title}
                onChange={(e) => setFeatureForm({...featureForm, title: e.target.value})}
                placeholder="Ex: Agenda intelligent"
              />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea
                value={featureForm.description}
                onChange={(e) => setFeatureForm({...featureForm, description: e.target.value})}
                placeholder="Description de la fonctionnalité..."
                rows={3}
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={featureForm.active}
                onCheckedChange={(checked) => setFeatureForm({...featureForm, active: checked})}
              />
              <Label>Active (visible sur le site)</Label>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setFeatureModalOpen(false)}>Annuler</Button>
            <Button onClick={handleSaveFeature} className="bg-blue-500 hover:bg-blue-600 text-white">
              <Save className="w-4 h-4 mr-2" /> Enregistrer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminVeterinairesContent;
